<template>
	<div>
		<h5 class="text-h5 q-mt-lg q-mb-md">{{ $t(game_data.game_name) }}</h5>

		<q-collapsible
			class="shadow-2 q-pa-sm q-ma-xs rounded-border"
			icon="far fa-ballot-check"
			right-icon="far fa-ballot-check"
			:label="$t('how_to_create_the_game')"
		>
			<div class="q-ma-sm">
				<p>{{ $t('this_or_that_instructions') }}</p>
				<p class="q-mt-lg">
					<em
						><strong>{{ `${$t('warning')}: ` }}</strong
						>{{ $t('create_game_before_save') }}</em
					>
				</p>
			</div>
		</q-collapsible>

		<div class="row">
			<div class="col-12 q-mb-md">
				<div class="row gutter-sm">
					<div class="col-12">
						<q-list
							v-if="game_data.options.length"
							dense
							bordered
							padding
							separator
							class="q-mt-md"
						>
							<q-item v-for="(option, index) in game_data.options" :key="index">
								<q-item-main class="q-mx-sm q-mt-sm row">
									<q-field :count="80" class="col-xs-12 col-md-4 col-4 q-px-sm">
										<q-input
											v-model="option.this.text"
											type="textarea"
											rows="2"
											:max-height="80"
											:float-label="$t('this')"
											@change="game_built ? resetGame() : null"
											:error="
												$v.game_data.options.$each[index].this.text.$error
											"
											@input="
												$v.game_data.options.$each[index].this.text.$touch
											"
										/>
									</q-field>
									<q-field :count="120" class="col-xs-12 col-md-4 col-4 q-px-sm">
										<q-input
											v-model="option.question"
											type="textarea"
											rows="2"
											:max-height="80"
											:float-label="$t('description')"
											@change="game_built ? resetGame() : null"
											:error="
												$v.game_data.options.$each[index].question.$error
											"
											@input="
												$v.game_data.options.$each[index].question.$touch
											"
										/>
									</q-field>
									<q-field :count="80" class="col-xs-12 col-md-4 col-4 q-px-sm">
										<q-input
											v-model="option.that.text"
											type="textarea"
											rows="2"
											:max-height="80"
											:float-label="$t('that')"
											@change="game_built ? resetGame() : null"
											:error="
												$v.game_data.options.$each[index].that.text.$error
											"
											@input="
												$v.game_data.options.$each[index].that.text.$touch
											"
										/>
									</q-field>
									<q-field class="col-12 q-my-sm">
										<div class="row justify-center">
											<div>
												<q-btn-toggle
													v-model="option.correct_choice"
													:options="selectors"
												/>
												<q-icon
													v-if="
														option.question.length &&
															option.this.text.length &&
															option.that.text.length
													"
													name="fal fa-check"
													color="positive"
													class="q-mx-md"
												/>
												<q-btn
													v-if="game_data.options.length > 2"
													round
													flat
													color="negative"
													icon="fal fa-trash"
													@click="removeWord(index)"
												>
													<q-tooltip>
														{{ $t('remove') }}
													</q-tooltip>
												</q-btn>
											</div>
										</div>
									</q-field>
								</q-item-main>
							</q-item>
						</q-list>

						<div class="row justify-end q-mr-md q-mt-md">
							<q-btn
								class="q-mr-md"
								color="primary"
								icon="far fa-plus"
								:label="$t('add')"
								@click="addOption"
								:disable="game_data.options.length > 9"
							/>
							<q-btn
								color="secondary"
								:label="$t('preview')"
								@click="buildGamePreview"
								:disable="disableGamePreview"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>

		<this-or-that-preview
			v-if="show_game_preview"
			:show_game_preview="show_game_preview"
			@hide_preview="hidePreview()"
			:game_name="game_data.game_name"
			:game_options="game_data.options"
		/>
	</div>
</template>

<script>
import ThisOrThatPreview from 'components/admin/games/ThisOrThatPreview'
import { required, maxLength } from 'vuelidate/lib/validators'

export default {
	name: 'AdminGameThisOrThat',

	props: ['can_save', 'saved_game_data', 'id_game'],

	components: {
		'this-or-that-preview': ThisOrThatPreview,
	},

	data() {
		return {
			game_data: {
				game_name: 'this_or_that',
				options: [
					{
						question: '',
						this: { text: '', value: 1 },
						that: { text: '', value: 2 },
						correct_choice: 1,
						correct: undefined,
						position: 0,
						selected: null,
					},
					{
						question: '',
						this: { text: '', value: 1 },
						that: { text: '', value: 2 },
						correct_choice: 1,
						correct: undefined,
						position: 1,
						selected: null,
					},
				],
			},
			selectors: [{ label: this.$t('this'), value: 1 }, { label: this.$t('that'), value: 2 }],
			show_game_preview: false,
			game_built: false,
		}
	},

	computed: {
		disableGamePreview() {
			if (this.game_data.options.length < 2) return true
			else {
				return this.game_data.options.filter(
					(o) => !o.this.text.length || !o.that.text.length || !o.question.length
				).length
					? true
					: false
			}
		},
	},

	methods: {
		buildGamePreview() {
			this.$v.$touch()
			if (!this.$v.$error) {
				this.show_game_preview = true
				this.game_built = true
				this.$emit('save_toggle', this.game_built)
				this.$emit('save_game_data', this.game_data)
			}
		},

		resetGame() {
			this.game_built = false
			this.$emit('save_toggle', this.game_built)
			this.$emit('save_game_data', null)
		},

		hidePreview() {
			this.show_game_preview = false
		},

		addOption() {
			const newSlot = {
				question: '',
				this: { text: '', value: 1 },
				that: { text: '', value: 2 },
				correct_choice: 1,
				correct: undefined,
				position: this.game_data.options.length,
				selected: null,
			}

			this.game_data.options.push(newSlot)
			if (this.game_built) {
				this.resetGame()
			}
		},

		removeWord(index) {
			this.game_data.options.splice(index, 1)
			if (this.game_built) this.resetGame()
		},
	},

	mounted() {
		if (this.saved_game_data) {
			this.game_data = this.saved_game_data
		} else {
			this.game_data = {
				game_name: 'this_or_that',
				options: [
					{
						question: '',
						this: { text: '', value: 1 },
						that: { text: '', value: 2 },
						correct_choice: 1,
						correct: undefined,
						position: 0,
						selected: null,
					},
					{
						question: '',
						this: { text: '', value: 1 },
						that: { text: '', value: 2 },
						correct_choice: 1,
						correct: undefined,
						position: 1,
						selected: null,
					},
				],
			}
		}
	},

	beforeDestroy() {
		this.resetGame()
	},

	validations() {
		return {
			game_data: {
				options: {
					$each: {
						question: { required, maxLength: maxLength(120) },
						this: { text: { required, maxLength: maxLength(80) } },
						that: { text: { required, maxLength: maxLength(80) } },
					},
				},
			},
		}
	},
}
</script>

<style></style>
